import { Controller } from "@hotwired/stimulus"
import Highcharts12 from "highcharts-v12";
import bullet12 from "highcharts-v12/modules/bullet"

export default class extends Controller {
  static values = {
    data: String,
    layout: String,
    layoutSettings: String
  }


  private resizeObserver: ResizeObserver | null = null;
  private currentWidth = 0;

  connect() {
    if (typeof bullet12 === 'function') {
      bullet12(Highcharts12)
    }
    
    this.initializeResizeObserver();
    this.renderDashboard();
  }

  disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  initializeResizeObserver() {
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newWidth = entry.contentRect.width;
        if (Math.abs(newWidth - this.currentWidth) > 5) {
          this.currentWidth = newWidth;
          this.renderDashboard();
        }
      }
    });
    
    if (this.resizeObserver) {
      this.resizeObserver.observe(this.element);
    }
  }

  renderDashboard() {
    const data = JSON.parse(this.dataValue)
    const layout = JSON.parse(this.layoutValue)
    const layoutSettings = JSON.parse(this.layoutSettingsValue)
    this.renderLayout(layout, data, layoutSettings)
  }

  renderLayout(layout, data, layoutSettings) {
    const settings = {
      colNum: this.getResponsiveColumnCount(layoutSettings?.colNum || 12),
      rowHeight: layoutSettings?.rowHeight || 30,
      gridItemColor: layoutSettings?.gridItemColor || "#F7F7F7"
    }

    const totalHeight = this.calculateTotalHeight(layout.grid, settings.rowHeight)

    const gridContainer = document.createElement("div")
    gridContainer.style.display = "grid"
    gridContainer.style.width = "100%"
    gridContainer.style.minHeight = `${totalHeight}px`
    gridContainer.style.gridTemplateColumns = `repeat(${settings.colNum}, 1fr)`
    gridContainer.style.gridAutoRows = `${settings.rowHeight}px`
    gridContainer.style.gap = "4px"
    gridContainer.style.backgroundColor = "white"
    gridContainer.style.position = "relative"
    gridContainer.style.overflow = "visible"
    gridContainer.style.boxSizing = "border-box"

    layout.grid.forEach(item => {
      const component = data.components.find(c => c.id === item.componentId)
      if (component?.type === "chart") {
        const gridItem = this.createGridItem(item, settings, false)
        this.renderChartContainer(gridItem, item)
        gridContainer.appendChild(gridItem)
      } else if (component?.type === "score") {
        const gridItem = this.createGridItem(item, settings, false)
        this.renderScoreComponent(component, gridItem, item.isCard)
        gridContainer.appendChild(gridItem)
      } else if (component?.type === "text") {
        const gridItem = this.createGridItem(item, settings, false)
        this.renderTextComponent(component, gridItem, item.isCard)
        gridContainer.appendChild(gridItem)
      }
    })

    this.element.innerHTML = ""
    this.element.appendChild(gridContainer)

    layout.grid.forEach(item => {
      const component = data.components.find(c => c.id === item.componentId)
      if (component?.type === "chart") {
        this.renderChart(component.highchartsData, item.componentId)
      }
    })
  }

  getResponsiveColumnCount(defaultCols) {
    const width = this.currentWidth || this.element.clientWidth
    
    if (width < 640) {
      return Math.min(defaultCols, 6)
    } else if (width < 1024) {
      return Math.min(defaultCols, 8)
    } else {
      return defaultCols
    }
  }

  calculateTotalHeight(grid, rowHeight) {
    const maxY = Math.max(...grid.map(item => item.y + item.h))
    return maxY * rowHeight
  }

  createGridItem(item, settings, withContainer = true) {
    const el = document.createElement("div")
    
    el.style.gridColumn = `${item.x + 1} / span ${item.w}`
    el.style.gridRow = `${item.y + 1} / span ${item.h}`
    el.style.backgroundColor = item.isCard ? "transparent" : (item.cardColor || "#fff")
    el.style.color = item.fontColor || "#000"
    el.style.padding = "8px"
    el.style.fontSize = "14px"
    el.style.overflow = item.isCard ? "visible" : "hidden"
    el.style.boxSizing = "border-box"
    
    return el
  }

  renderChartContainer(container, item) {
    const chartDiv = document.createElement("div")
    chartDiv.id = item.componentId
    chartDiv.style.width = "100%"
    chartDiv.style.height = "100%"
    if (item.isCard) {
      const cardDiv = document.createElement("div")
      cardDiv.style.background = "#fff"
      cardDiv.style.color = "#000"
      cardDiv.style.boxShadow = "rgba(0, 7, 15, 0.1) 5px 5px 20px 5px"
      cardDiv.style.borderRadius = "8px"
      const containerWidth = this.currentWidth || this.element.clientWidth
      const padding = containerWidth < 640 ? "1rem" : "1.5rem"
      cardDiv.style.padding = padding
      cardDiv.style.width = "100%"
      cardDiv.style.height = "100%"
      cardDiv.style.display = "flex"
      cardDiv.style.alignItems = "center"
      cardDiv.style.justifyContent = "center"
      cardDiv.appendChild(chartDiv)
      container.appendChild(cardDiv)
    } else {
      container.appendChild(chartDiv)
    }
  }

  renderChart(chartData, targetId) {
    const chartContainer = document.getElementById(targetId)
    if (chartContainer) {
      Highcharts12.setOptions({
        lang: {
          decimalPoint: ','
        },
        credits: {
          enabled: false
        },
        xAxis: {
          labels: {
            style: {
              fontSize: '14px',
            }
          }
        },
        chart: {
          animation: false,
          backgroundColor: 'transparent'
        },
        plotOptions: {
          series: {
            animation: false
          }
        }
      })

      const updatedChartData = {
        ...chartData,
        title: {
          text: ''
        }
      }

      Highcharts12.chart(chartContainer, updatedChartData)
    } else {
      console.error("Chart container not found for id:", targetId)
    }
  }

  renderScoreComponent(component, container, isCard = false) {
    const wrapper = document.createElement("div")
    wrapper.className = "score-component"
    wrapper.style.fontFamily = "Inter"
    wrapper.style.display = "flex"
    wrapper.style.flexDirection = "column"
    wrapper.style.alignItems = "center"
    wrapper.style.justifyContent = "center"
    wrapper.style.height = "100%"
    wrapper.style.width = "100%"
    wrapper.style.overflow = "hidden"
    wrapper.style.boxSizing = "border-box"
    
    if (isCard) {
      wrapper.style.background = "#fff"
      wrapper.style.color = "#000"
      wrapper.style.boxShadow = "rgba(0, 7, 15, 0.1) 5px 5px 20px 5px"
      wrapper.style.borderRadius = "8px"
      const containerWidth = this.currentWidth || this.element.clientWidth
      const padding = containerWidth < 640 ? "0.5rem" : "2rem"
      wrapper.style.padding = padding
      wrapper.style.marginTop = "-10px"
    }

    if (component.image) {
      const imgContainer = document.createElement("div")
      imgContainer.className = "image-container mb-4"
      const img = document.createElement("img")
      img.src = component.image
      img.style.height = "64px"
      img.style.maxWidth = "100%"
      img.style.display = "block"
      img.style.margin = "0 auto"
      imgContainer.appendChild(img)
      wrapper.appendChild(imgContainer)
    }

    const valuesDiv = document.createElement("div")
    valuesDiv.className = "values"
    valuesDiv.style.display = "flex"
    valuesDiv.style.justifyContent = "center"
    valuesDiv.style.alignItems = "center"
    valuesDiv.style.flexWrap = "wrap"
    valuesDiv.style.width = "100%"

    const h2 = document.createElement("h2")
    const containerWidth = this.currentWidth || this.element.clientWidth
    const fontSize = this.getResponsiveFontSize(containerWidth, 70)
    h2.style.fontSize = `${fontSize}px`
    h2.style.margin = "0"
    h2.style.lineHeight = "1"
    h2.style.textAlign = "center"
    h2.style.wordBreak = "break-word"
    h2.style.overflowWrap = "break-word"
    const value = component.value
    if (value == 0 || value == null) {
      h2.textContent = "(-)"
    } else {
      h2.textContent = `${Number(value).toLocaleString('de-DE', {
        minimumFractionDigits: component.minimumFractionDigits,
        maximumFractionDigits: component.maximumFractionDigits
      })} ${component.unit || ''}`
    }
    valuesDiv.appendChild(h2)

    if (component.valueCompare !== undefined) {
      const compareDiv = document.createElement("div")
      compareDiv.className = "compare-value"
      const compareFontSize = this.getResponsiveFontSize(containerWidth, 35)
      compareDiv.style.fontSize = `${compareFontSize}px`
      compareDiv.style.fontWeight = "600"
      compareDiv.style.color = "#919399"
      compareDiv.style.alignSelf = "flex-end"
      compareDiv.style.marginBottom = "10px"
      compareDiv.style.wordBreak = "break-word"
      compareDiv.style.overflowWrap = "break-word"
      if (component.valueCompare == 0 || component.value == null) {
        compareDiv.textContent = "(-)"
      } else if (component.valueCompare != component.value) {
        compareDiv.textContent = `(${Number(component.valueCompare).toLocaleString('de-DE', {
          minimumFractionDigits: component.minimumFractionDigits,
          maximumFractionDigits: component.maximumFractionDigits
        })} ${component.unit || ''})`
      }
      valuesDiv.appendChild(compareDiv)
    }

    wrapper.appendChild(valuesDiv)

    if (component.text) {
      const textDiv = document.createElement("div")
      textDiv.className = "text"
      textDiv.style.marginTop = "0rem"
      textDiv.style.textAlign = "center"
      textDiv.style.wordBreak = "break-word"
      textDiv.style.overflowWrap = "break-word"
      textDiv.innerHTML = component.text
      wrapper.appendChild(textDiv)
    }

    if (component.delta) {
      const p = document.createElement("p")
      p.style.display = "flex"
      p.style.justifyContent = "center"
      p.style.alignItems = "baseline"
      p.style.flexWrap = "wrap"
      const iconSpan = document.createElement("span")
      iconSpan.style.margin = "10px"
      if (component.delta.value == 0) {
        iconSpan.textContent = "→"
      } else if (component.delta.value > 0) {
        iconSpan.textContent = "↗"
      } else {
        iconSpan.textContent = "↘"
      }
      p.appendChild(iconSpan)
      const labelSpan = document.createElement("span")
      labelSpan.style.wordBreak = "break-word"
      labelSpan.style.overflowWrap = "break-word"
      labelSpan.innerHTML = component.delta.value == null ? "-" : (component.delta.label || "")
      p.appendChild(labelSpan)
      wrapper.appendChild(p)
    }

    container.appendChild(wrapper)
  }

  getResponsiveFontSize(containerWidth, defaultSize) {
    if (containerWidth < 640) {
      return Math.max(defaultSize * 0.5, 16)
    } else if (containerWidth < 1024) {
      return Math.max(defaultSize * 0.7, 24)
    } else {
      return defaultSize
    }
  }

  renderTextComponent(component, container, isCard = false) {
    const txtDiv = document.createElement("div")
    txtDiv.className = "txt"
    txtDiv.style.fontFamily = "Inter"
    txtDiv.style.display = "flex"
    txtDiv.style.alignItems = "center"
    txtDiv.style.height = "100%"
    txtDiv.style.width = "100%"
    txtDiv.style.overflow = "hidden"
    txtDiv.style.boxSizing = "border-box"
    
    const containerWidth = this.currentWidth || this.element.clientWidth
    const fontSize = this.getResponsiveFontSize(containerWidth, 14)
    txtDiv.style.fontSize = `${fontSize}px`
    
    if (isCard) {
      txtDiv.style.background = "#fff"
      txtDiv.style.color = "#000"
      txtDiv.style.boxShadow = "rgba(0, 7, 15, 0.1) 5px 5px 20px 5px"
      txtDiv.style.borderRadius = "8px"
      const padding = containerWidth < 640 ? "1rem" : "2rem"
      txtDiv.style.padding = padding
      txtDiv.style.marginTop = "-10px"
    }
    txtDiv.style.wordBreak = "break-word"
    txtDiv.style.overflowWrap = "break-word"
    txtDiv.innerHTML = component.text || ""
    container.appendChild(txtDiv)
  }
}
